import lodash from "lodash";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { AvailableBaseRoutes } from "../../../utils/constants";
import { IGroupLean } from "../../../domain/group/group-lean";
import { IPaginationMeta } from "../../../domain/pagination/pagination-meta";
import { IGetGroupsLeanFilter } from "../../../repositories/models/group/get-group.model";
import { useGroupContext } from "./group-context";

interface IGroupTableContext {
  groups: IGroupLean[];
  isLoading: boolean;
  entryCount: number;
  tablePagination: IPaginationMeta;
  setTablePagination: (pagination: IPaginationMeta) => void;
  filter: IGetGroupsLeanFilter;
  setFilter: (filters: IGetGroupsLeanFilter) => void;
  onShowGroupDetail: (erpNumber: string) => void;
  isFilterDirty: boolean;
  resetFilter: () => void;
}

export const GroupTableContext = createContext<IGroupTableContext>(
  {} as IGroupTableContext,
);

export const useGroupTableContext = () => {
  return useContext(GroupTableContext);
};

const useGroupTableProvider = (): IGroupTableContext => {
  const {
    isLoading,
    tablePagination,
    setTablePagination,
    filter,
    setFilter,
    groups,
  } = useGroupContext();
  const [entryCount, setEntryCount] = useState<number>(50);
  const history = useHistory();

  useEffect(() => {
    setEntryCount(groups && groups.length > 50 ? 50 : groups.length);
  }, [groups]);

  const onShowGroupDetail = (erpNumber: string) => {
    history.push(AvailableBaseRoutes.GroupDetail.replace(":erpNumber", erpNumber));
  };

  const isFilterDirty = useMemo(
    () =>
      !lodash.isEmpty(filter?.nameQuery) || !lodash.isEmpty(filter?.erpQuery) || filter?.groupType !== undefined,
    [filter],
  );

  const resetFilter = () => {
    setTablePagination({ ...tablePagination, page: 0 });
    setFilter({
      nameQuery: undefined,
      erpQuery: undefined,
      groupType: undefined,
    });
  };

  return {
    groups,
    setTablePagination,
    filter,
    setFilter,
    tablePagination,
    entryCount,
    isLoading,
    onShowGroupDetail,
    isFilterDirty,
    resetFilter,
  };
};

export const GroupTableProvider: React.FC<React.PropsWithChildren> = (props) => {
  const value = useGroupTableProvider();
  return (
    <GroupTableContext.Provider value={value}>
      {props.children}
    </GroupTableContext.Provider>
  );
};
